<template>
  <v-col>
    <Loader v-show="loading" :loading="loading" />
    <v-card v-if="errorException">
      <v-row align="center" class="pa-3 ma-3 ">
        <v-icon small color="orange darken-3">
          report_problem
        </v-icon>
        <span class="orange--text text--darken-3 align-center">{{
          errorException
        }}</span>
        <v-spacer />
        <IconBtn
          icons="close"
          :large="true"
          color="grey darken-4"
          @click="close"
        />
      </v-row>
    </v-card>
    <v-card v-else>
      <v-card-title>
        <v-row no-gutters>
          <span class="headline font-weight-medium">{{ header }}</span>
          <v-spacer />
          <IconBtn
            icons="close"
            :large="true"
            color="grey darken-4"
            @click="close"
          />
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-hover v-if="!tagEditMode && !isNew" v-slot="{ hover }">
          <div
            :elevation="hover ? 12 : 2"
            :class="{ 'on-hover': hover, 'hover-block': true }"
          >
            <ChipsTag
              v-for="tag in tags"
              :key="tag.id"
              :value="tag"
              @update="updateTag"
              @delete="deleteTag"
            />
            <span v-if="block && !block.tags.length">Додати теги</span>
            <HoverBtn :hover="hover" @handler="tagEditMode = true" />
          </div>
        </v-hover>
        <SelectTags
          v-else
          v-model="tags"
          :add-btn="true"
          :edit="!isNew"
          :multiple-select="true"
          class="mb-2"
          @input="isChanged = true"
          @edit="updateTags"
          @close="closeEditTagMode"
          @add="addTags"
        />
        <Markdown :id="id" v-model="content" @input="isChanged = true" />
      </v-card-text>
      <v-card-actions>
        <PrimaryBtn :loading="loading" :disabled="!isChanged" @click="save" />
        <PrimaryBtn
          v-if="!isNew"
          text="Зберегти версію"
          :loading="versionLoading"
          :disabled="isNew || !isChanged"
          @click="saveVersion"
        />
      </v-card-actions>
    </v-card>
  </v-col>
</template>
<script>
import PrimaryBtn from "../../components/PrimaryBtn.vue";
import IconBtn from "../../components/IconBtn.vue";
import ChipsTag from "../tags/ChipsTag.vue";
import SelectTags from "../tags/SelectTags.vue";
import HoverBtn from "../../components/HoverBtn.vue";
import Loader from "../../components/Loader.vue";
import * as blocksApi from "../../backend/document-blocksApi.js";
import Markdown from "../../components/markdown/Markdown.vue";
export default {
  components: {
    PrimaryBtn,
    IconBtn,
    ChipsTag,
    SelectTags,
    HoverBtn,
    Loader,
    Markdown,
  },
  props: ["id", "modal"],
  data: () => ({
    tags: [],
    content: "",
    block: null,
    loading: false,
    versionLoading: false,
    isChanged: false,
    tagEditMode: false,
    errorException: "",
  }),
  computed: {
    isNew() {
      return this.id != 0 ? false : true;
    },
    header() {
      return this.isNew ? "Створення блоку" : "Редагування блоку";
    },
  },
  created() {
    this.getDocumentBlock();
  },
  beforeMount() {
    window.addEventListener("beforeunload", this.close);
    this.$once("hook:beforeDestroy", () => {
      window.removeEventListener("beforeunload", this.close);
    });
  },
  mounted() {
    window.addEventListener("unload", this.close);
  },
  beforeDestroy() {
    window.removeEventListener("unload", this.close);
    this.unblockUpdate();
  },
  methods: {
    async getDocumentBlock() {
      if (this.isNew) {
        return;
      }

      this.loading = true;
      try {
        const result = await blocksApi.getDocumentBlock(this.id);
        this.block = result;
        this.tags = result.tags;
        this.content = result.content;
        this.loading = false;
      } catch (err) {
        this.errorException = err.response.data;
        this.$notifyWarning(err.response.data);
        this.loading = false;
      }
    },
    async save() {
      this.loading = true;
      const block = {
        content: this.content,
        tags: [...new Set(this.tags.map((t) => t.id))],
      };
      let newId = null;
      try {
        if (!this.isNew) {
          await blocksApi.updateDocumentBlock(this.id, block);
        } else {
          newId = await blocksApi.createDocumentBlock(block);
        }
        if (this.modal) {
          this.$emit("add", this.isNew ? { blockId: newId } : "");
        } else {
          this.$router.go(-1);
        }
        this.$notifySuccess();
      } catch {
        this.$notifyWarning();
      } finally {
        this.loading = false;
        this.isChanged = false;
        this.content = "";
        this.tags = [];
      }
    },
    async saveVersion() {
      if (this.isNew) {
        return;
      }

      this.loading = true;
      const version = {
        parentBlocks: [this.id],
        content: this.content,
        tags: [...new Set(this.tags.map((t) => t.id))],
      };
      try {
        const versionId = await blocksApi.createDocumentBlockVersion(version);
        if (!this.modal && versionId) {
          this.$router.push({ path: `/gen-doc-blocks/${versionId}` });
        }
        if (this.modal) {
          this.$emit("update", versionId);
        }
        this.$notifySuccess();
      } catch {
        this.$notifyWarning();
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
    updateTag(tag) {
      this.tags.forEach((t) => {
        if (t.id !== tag.id) {
          return;
        }
        t.title = tag.title;
      });
      this.$notifySuccess();
    },
    deleteTag(tag) {
      const index = this.tags.findIndex((t) => t.id === tag.id);
      this.tags.splice(index, 1);
      this.$notifySuccess();
    },
    async updateTags() {
      this.loading = true;
      await blocksApi.updateDocumentBlock(this.id, {
        content: this.content,
        tags: [...new Set(this.tags.map((t) => t.id))],
      });
      this.block.tags = this.tags;
      this.tagEditMode = false;
      this.isChanged = false;
      this.loading = false;
      if (this.modal) {
        this.$emit("add");
        return;
      }
      this.$notifySuccess();
    },
    closeEditTagMode() {
      this.tags = this.block.tags;
      this.tagEditMode = false;
    },
    addTags(values) {
      this.tags = [...this.tags, ...values];
      this.tags = this.tags.filter(
        (v, i, a) => a.findIndex((t) => t.id === v.id) === i
      );
      this.isChanged = true;
      this.$notifySuccess();
    },
    async unblockUpdate() {
      if (!this.errorException && !this.isNew) {
        await blocksApi.unblockUpdate(this.id);
      }
    },
    async close() {
      if (!this.errorException && !this.isNew) {
        await this.unblockUpdate();
      }
      if (this.modal) {
        this.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
  },
};
</script>
